import type { RefObject } from "react";
import type { Editor } from "@tiptap/core";
import {
  APOSTROPHE,
  BOLD,
  BULLET_LIST,
  DIRECTIVE,
  DIV,
  EMPHASIZED,
  ITALIC,
  ORDERED_LIST,
  STRONG,
  UNDOREDO,
  VARIABLE,
  type ToolbarButtonId,
} from "../shared/richTextEditorToolbarConstants";
import { runFormat } from "../shared/richTextEditorFormatUtils";
import {
  RTE_TOOLTIP_BOLD,
  RTE_TOOLTIP_ITALIC,
  RTE_TOOLTIP_STRONG,
  RTE_TOOLTIP_EMPHASIZED,
  RTE_TOOLTIP_BULLET_LIST,
  RTE_TOOLTIP_ORDERED_LIST,
  RTE_TOOLTIP_INSERT_APOSTROPHE,
  RTE_TOOLTIP_INSERT_HORIZONTAL_RULE,
} from "@/constants/uiCopy/richTextEditor";
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  List,
  ListOrdered,
  SeparatorHorizontal,
} from "lucide-react";
import type { Variables } from "@/lib/sparxs/metadataSchemas";
import type { DirectiveConfigItem } from "../../directives/core/config";
import { htmlToMarkdown } from "@/lib/markdown/sparxMarkdownHtml";
import { plainDocToMarkdown } from "@/lib/markdown/markdownPlainDoc";
import { IconWithAccessibilityBadge } from "../shared/iconWithAccessibilityBadge";
import { ToolbarIconTooltipAction } from "../shared/richTextEditorToolbarIconTooltipAction";
import { RichTextEditorToolbarUndoRedo } from "../footer/richTextEditorToolbarUndoRedo";
import {
  RichTextEditorToolbarDirectivePopover,
  RichTextEditorToolbarVariablePopover,
} from "../shared/richTextEditorToolbarVariableAndDirective";
import type { getRteShortcutKeys } from "../shared/richTextEditorToolbarShortcuts";

export type RichTextEditorToolbarStateSnapshot = {
  canUndo: boolean;
  canRedo: boolean;
  isBold: boolean;
  isItalic: boolean;
  isStrong: boolean;
  isEmphasized: boolean;
  isBulletList: boolean;
  isOrderedList: boolean;
};

type ShortcutKeys = ReturnType<typeof getRteShortcutKeys>;

export const richTextEditorHeaderToolbarButtons = (args: {
  buttons: ToolbarButtonId[];
  editor: Editor;
  isWysiwyg: boolean;
  modKeyLabel: string;
  shortcutKeys: ShortcutKeys;
  toolbarState: RichTextEditorToolbarStateSnapshot | null;
  variablePopoverOpen: boolean;
  setVariablePopoverOpen: (open: boolean) => void;
  directivePopoverOpen: boolean;
  setDirectivePopoverOpen: (open: boolean) => void;
  stableVariables: Variables[];
  availableDirectives: DirectiveConfigItem[];
  onChange: (markdown: string) => void;
  handleInsertVariable: (variableName: string) => void;
  handleInsertDirective: (directiveKey: string) => void;
  justInsertedDirectiveRef: RefObject<boolean>;
}) => {
  const {
    buttons,
    editor,
    isWysiwyg,
    modKeyLabel,
    shortcutKeys,
    toolbarState,
    variablePopoverOpen,
    setVariablePopoverOpen,
    directivePopoverOpen,
    setDirectivePopoverOpen,
    stableVariables,
    availableDirectives,
    onChange,
    handleInsertVariable,
    handleInsertDirective,
    justInsertedDirectiveRef,
  } = args;

  return buttons.map((buttonId) => {
    switch (buttonId) {
      case UNDOREDO: {
        const canUndo = toolbarState?.canUndo ?? false;
        const canRedo = toolbarState?.canRedo ?? false;
        return (
          <RichTextEditorToolbarUndoRedo
            key={UNDOREDO}
            editor={editor}
            modKeyLabel={modKeyLabel}
            canUndo={canUndo}
            canRedo={canRedo}
          />
        );
      }
      case BOLD: {
        const isBold = toolbarState?.isBold ?? false;
        return (
          <ToolbarIconTooltipAction
            key={BOLD}
            tooltipLabel={RTE_TOOLTIP_BOLD}
            shortcutKeys={shortcutKeys.bold}
            ariaLabel="Bold"
            ariaPressed={isWysiwyg ? isBold : undefined}
            onClick={() =>
              runFormat(isWysiwyg, editor, {
                toggle: () => editor.chain().focus().toggleBold().run(),
                wrap: { open: "**", close: "**" },
              })
            }
          >
            <BoldIcon className="size-4" />
          </ToolbarIconTooltipAction>
        );
      }
      case ITALIC: {
        const isItalic = toolbarState?.isItalic ?? false;
        return (
          <ToolbarIconTooltipAction
            key={ITALIC}
            tooltipLabel={RTE_TOOLTIP_ITALIC}
            shortcutKeys={shortcutKeys.italic}
            ariaLabel="Italic"
            ariaPressed={isWysiwyg ? isItalic : undefined}
            onClick={() =>
              runFormat(isWysiwyg, editor, {
                toggle: () => editor.chain().focus().toggleItalic().run(),
                wrap: { open: "*", close: "*" },
              })
            }
          >
            <ItalicIcon className="size-4" />
          </ToolbarIconTooltipAction>
        );
      }
      case STRONG: {
        const isStrong = toolbarState?.isStrong ?? false;
        return (
          <ToolbarIconTooltipAction
            key={STRONG}
            tooltipLabel={RTE_TOOLTIP_STRONG}
            shortcutKeys={[]}
            ariaLabel={RTE_TOOLTIP_STRONG}
            ariaPressed={isWysiwyg ? isStrong : undefined}
            onClick={() =>
              runFormat(isWysiwyg, editor, {
                toggle: () => editor.chain().focus().toggleBold().run(),
                wrap: { open: "__", close: "__" },
              })
            }
          >
            <IconWithAccessibilityBadge className="size-4">
              <BoldIcon className="size-4" />
            </IconWithAccessibilityBadge>
          </ToolbarIconTooltipAction>
        );
      }
      case EMPHASIZED: {
        const isEmphasized = toolbarState?.isEmphasized ?? false;
        return (
          <ToolbarIconTooltipAction
            key={EMPHASIZED}
            tooltipLabel={RTE_TOOLTIP_EMPHASIZED}
            shortcutKeys={[]}
            ariaLabel={RTE_TOOLTIP_EMPHASIZED}
            ariaPressed={isWysiwyg ? isEmphasized : undefined}
            onClick={() =>
              runFormat(isWysiwyg, editor, {
                toggle: () => editor.chain().focus().toggleItalic().run(),
                wrap: { open: "_", close: "_" },
              })
            }
          >
            <IconWithAccessibilityBadge className="size-4">
              <ItalicIcon className="size-4" />
            </IconWithAccessibilityBadge>
          </ToolbarIconTooltipAction>
        );
      }
      case BULLET_LIST: {
        const isBulletList = toolbarState?.isBulletList ?? false;
        return (
          <ToolbarIconTooltipAction
            key={BULLET_LIST}
            tooltipLabel={RTE_TOOLTIP_BULLET_LIST}
            shortcutKeys={shortcutKeys.bulletList}
            ariaLabel={RTE_TOOLTIP_BULLET_LIST}
            ariaPressed={isWysiwyg ? isBulletList : undefined}
            onClick={() => {
              if (isWysiwyg) {
                editor.chain().focus().toggleBulletList().run();
                onChange(htmlToMarkdown(editor.getHTML()));
              } else {
                editor.chain().focus().insertContent("- ").run();
                onChange(plainDocToMarkdown(editor.state.doc));
              }
            }}
          >
            <List className="size-4" />
          </ToolbarIconTooltipAction>
        );
      }
      case ORDERED_LIST: {
        const isOrderedList = toolbarState?.isOrderedList ?? false;
        return (
          <ToolbarIconTooltipAction
            key={ORDERED_LIST}
            tooltipLabel={RTE_TOOLTIP_ORDERED_LIST}
            shortcutKeys={shortcutKeys.orderedList}
            ariaLabel={RTE_TOOLTIP_ORDERED_LIST}
            ariaPressed={isWysiwyg ? isOrderedList : undefined}
            onClick={() => {
              if (isWysiwyg) {
                editor.chain().focus().toggleOrderedList().run();
                onChange(htmlToMarkdown(editor.getHTML()));
              } else {
                editor.chain().focus().insertContent("1. ").run();
                onChange(plainDocToMarkdown(editor.state.doc));
              }
            }}
          >
            <ListOrdered className="size-4" />
          </ToolbarIconTooltipAction>
        );
      }
      case APOSTROPHE: {
        const typographicApostrophe = "\u2019";
        return (
          <ToolbarIconTooltipAction
            key={APOSTROPHE}
            tooltipLabel={RTE_TOOLTIP_INSERT_APOSTROPHE}
            shortcutKeys={[]}
            ariaLabel={RTE_TOOLTIP_INSERT_APOSTROPHE}
            onClick={() => {
              if (isWysiwyg) {
                editor
                  .chain()
                  .focus()
                  .insertContent(typographicApostrophe)
                  .run();
                onChange(htmlToMarkdown(editor.getHTML()));
              } else {
                editor
                  .chain()
                  .focus()
                  .insertContent(typographicApostrophe)
                  .run();
                onChange(plainDocToMarkdown(editor.state.doc));
              }
            }}
          >
            <span className="font-serif text-base" aria-hidden>
              {typographicApostrophe}
            </span>
          </ToolbarIconTooltipAction>
        );
      }
      case DIV: {
        return (
          <ToolbarIconTooltipAction
            key={DIV}
            tooltipLabel={RTE_TOOLTIP_INSERT_HORIZONTAL_RULE}
            shortcutKeys={shortcutKeys.horizontalRule}
            ariaLabel="Insert horizontal rule"
            onClick={() => {
              if (isWysiwyg) {
                editor.chain().focus().setHorizontalRule().run();
                onChange(htmlToMarkdown(editor.getHTML()));
              } else {
                editor.chain().focus().insertContent("\n\n---\n\n").run();
                onChange(plainDocToMarkdown(editor.state.doc));
              }
            }}
          >
            <SeparatorHorizontal className="size-4" />
          </ToolbarIconTooltipAction>
        );
      }
      case VARIABLE: {
        if (!isWysiwyg) return null;
        return (
          <RichTextEditorToolbarVariablePopover
            key={VARIABLE}
            modKeyLabel={modKeyLabel}
            open={variablePopoverOpen}
            onOpenChange={setVariablePopoverOpen}
            variables={stableVariables}
            onInsertVariable={handleInsertVariable}
          />
        );
      }
      case DIRECTIVE: {
        if (!isWysiwyg) return null;
        return (
          <RichTextEditorToolbarDirectivePopover
            key={DIRECTIVE}
            modKeyLabel={modKeyLabel}
            open={directivePopoverOpen}
            onOpenChange={setDirectivePopoverOpen}
            availableDirectives={availableDirectives}
            onInsertDirective={handleInsertDirective}
            justInsertedDirectiveRef={justInsertedDirectiveRef}
          />
        );
      }
      default:
        return null;
    }
  });
};
